import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Command, CommandBase } from '../../../core/model/command';
import { CommandController } from '../../../core/component/base/command-controller.base';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { SelectionItem } from '../../../core/component/select/select.component';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandHandlerResolver } from '../../../core/service/command/command-handler-resolver.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { SidebarService } from '../../../core/service/sidebar.service';
import { LocalDate } from '../../../core/model/local-date';
import { BlockRentalObject } from '../../model/block-rental-object';

enum RentalObjectCommands {
  edit = 'editBlockContractRentalObject',
  openRentalObject = 'openBlockContractRentalObject',
  delete = 'deleteBlockContractRentalObject',
}

class RentalObjectListItem {
  constructor(
    readonly form: FormGroup,
    readonly index: number,
    readonly rentalId: string,
    readonly street: string,
    readonly rentalObjectTypeName: string,
    readonly propertyNumberName: string,
    readonly companyNumberName: string,
    readonly categoryCaption: string,
    readonly fromDate: LocalDate,
    readonly toDate: LocalDate
  ) {}
}

@Component({
  selector: 'app-block-contract-rental-objects',
  templateUrl: './block-contract-rental-objects.component.html',
  styleUrls: ['./block-contract-rental-objects.component.scss'],
  providers: [CommandDispatcher],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BlockContractRentalObjectsComponent extends CommandController implements OnChanges, OnDestroy {
  @ViewChild('rentalObjectSidebar', { static: false }) rentalObjectSidebar: TemplateRef<any>;

  @Input() rentalObjectForms: FormArray;
  private _categories: SelectionItem[] = [];
  @Input() set categories(value: SelectionItem[] | null | undefined) {
    this._categories = value ?? [];
  }
  get categories(): SelectionItem[] {
    return this._categories;
  }
  private _rentalObjects: BlockRentalObject[] = [];
  @Input() set rentalObjects(value: BlockRentalObject[] | null | undefined) {
    this._rentalObjects = value ?? [];
  }
  get rentalObjects(): BlockRentalObject[] {
    return this._rentalObjects;
  }
  @Input() getObjectSelection: (form: FormGroup) => SelectionItem[];
  @Input() selectionVersion = 0;
  @Output() addRentalObject = new EventEmitter<void>();
  @Output() removeRentalObject = new EventEmitter<number>();
  @Output() openRentalObject = new EventEmitter<FormGroup>();

  readonly columns: ColumnDefinition[];
  readonly menu = [
    new MenuItemDefinition('EDIT_BTN', RentalObjectCommands.edit),
    new MenuItemDefinition('COMMON.OPEN_RENTAL_OBJECT', RentalObjectCommands.openRentalObject),
    new MenuItemDefinition('DELETE_BTN', RentalObjectCommands.delete),
  ];
  readonly displayForm: FormGroup;
  visibleRows: RentalObjectListItem[] = [];
  selectedRow: RentalObjectListItem;
  private _isNew = false;
  private _originalFormValue: { [key: string]: any } = null;
  private _formChanges = new Subscription();

  constructor(
    handlerResolver: CommandHandlerResolver,
    commandDispatcher: CommandDispatcher,
    columnFactory: ColumnDefinitionFactory,
    fb: FormBuilder,
    controls: FormControlExFactory,
    private readonly _sidebar: SidebarService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    super(commandDispatcher, handlerResolver);
    this.columns = [
      columnFactory.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      columnFactory.create('LEASE_AGREEMENT.RENTAL_OBJECT', 'street'),
      columnFactory.create('RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectTypeName'),
      columnFactory.createDate('LEASE_AGREEMENT.OBJECT_INCLUDED_FROM', 'fromDate'),
      columnFactory.createDate('LEASE_AGREEMENT.OBJECT_INCLUDED_TO', 'toDate'),
      columnFactory.create('COMMON.PROPERTY', 'propertyNumberName', ColumnVisibility.optional),
      columnFactory.create('COMMON.COMPANY', 'companyNumberName', ColumnVisibility.optional),
      columnFactory.createTranslation('COMMON.RENTAL_OBJECT_CATEGORY', 'categoryCaption', ColumnVisibility.optional),
    ];
    this.displayForm = fb.group({
      displayHistory: controls.create('COMMON.DISPLAY_HISTORY', false),
    });
    this.AddSubscription(this.displayForm.controls['displayHistory'].valueChanges.subscribe(() => this.refreshRows()));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rentalObjectForms']) {
      this._formChanges.unsubscribe();
      this._formChanges = new Subscription();
      if (this.rentalObjectForms) {
        this._formChanges.add(this.rentalObjectForms.valueChanges.subscribe(() => this.refreshRows()));
      }
    }
    this.refreshRows();
  }

  override ngOnDestroy(): void {
    if (this._sidebar.hasSidebarContent(this.rentalObjectSidebar)) {
      this._sidebar.toggleSidebar(false);
    }
    this.clearEditorState();
    this._formChanges.unsubscribe();
    super.ngOnDestroy();
  }

  canExecute(command: CommandBase): boolean {
    if (command.id === RentalObjectCommands.edit) return true;
    if (command.id === RentalObjectCommands.openRentalObject) return true;
    if (command.id === RentalObjectCommands.delete) return this.rentalObjectForms?.length > 1;
    return false;
  }

  execute(command: CommandBase): void {
    const row = (command as Command).context as RentalObjectListItem;
    if (command.id === RentalObjectCommands.edit) this.openEditor(row, false);
    if (command.id === RentalObjectCommands.openRentalObject) this.openRentalObject.emit(row.form);
    if (command.id === RentalObjectCommands.delete) this.deleteRow(row);
  }

  createRow(): void {
    this.addRentalObject.emit();
    const form = this.rentalObjectForms?.at(this.rentalObjectForms.length - 1) as FormGroup;
    if (form) this.openEditor(this.createListItem(form, this.rentalObjectForms.length - 1), true);
  }

  onRowClicked(event: { context: RentalObjectListItem }): void {
    this.openEditor(event.context, false);
  }

  confirmEditor(): void {
    this.selectedRow?.form.markAllAsTouched();
    if (this.selectedRow?.form.invalid) return;

    this.finishEditor(true);
    this.refreshRows();
  }

  cancelEditor(closeSidebar = true): void {
    const row = this.selectedRow;
    const isNew = this._isNew;
    const originalFormValue = this._originalFormValue;
    if (!isNew && row && originalFormValue) this.restoreFormValue(row.form, originalFormValue);
    this.finishEditor(closeSidebar);
    if (isNew && row) this.removeRentalObject.emit(row.index);
    this.refreshRows();
  }

  private openEditor(row: RentalObjectListItem, isNew: boolean): void {
    this.selectedRow = row;
    this._isNew = isNew;
    this._originalFormValue = isNew ? null : this.createFormValueSnapshot(row.form);
    this.refreshRows();
    this._sidebar.setSidebarContent({ template: this.rentalObjectSidebar, onClose: () => this.cancelEditor(false) });
    this._sidebar.toggleSidebar(true);
  }

  private deleteRow(row: RentalObjectListItem): void {
    if (this.rentalObjectForms?.length <= 1) return;
    this.removeRentalObject.emit(row.index);
    this.finishEditor(true);
    this.refreshRows();
  }

  private finishEditor(closeSidebar: boolean): void {
    this.clearEditorState();
    if (closeSidebar) this._sidebar.toggleSidebar(false);
  }

  private clearEditorState(): void {
    this.selectedRow = null;
    this._isNew = false;
    this._originalFormValue = null;
  }

  private createFormValueSnapshot(form: FormGroup): { [key: string]: any } {
    const snapshot: { [key: string]: any } = {};
    Object.keys(form.controls).forEach(key => (snapshot[key] = this.cloneValue(form.controls[key].value)));
    return snapshot;
  }

  private restoreFormValue(form: FormGroup, snapshot: { [key: string]: any }): void {
    Object.keys(snapshot).forEach(key => form.controls[key]?.setValue(this.cloneValue(snapshot[key])));
  }

  private cloneValue(value: any): any {
    if (value instanceof Date) return new Date(value.getTime());
    return typeof value?.clone === 'function' ? value.clone() : value;
  }

  private refreshRows(): void {
    if (!this.rentalObjectForms) {
      this.visibleRows = [];
      return;
    }

    let rows = this.rentalObjectForms.controls
      .map((control, index) => this.createListItem(control as FormGroup, index));
    if (this._isNew && this.selectedRow) rows = rows.filter(row => row.form !== this.selectedRow.form);
    const showHistory = this.displayForm.controls['displayHistory'].value;
    this.visibleRows = showHistory ? rows : rows.filter(row => !this.isHistorical(row));
    this._cdr.detectChanges();
  }

  private createListItem(form: FormGroup, index: number): RentalObjectListItem {
    const category = form.controls['category'].value;
    const rentalObjectId = form.controls['rentalObjectId'].value;
    const rentalObject = this.rentalObjects.find(item => item.id === rentalObjectId);
    return new RentalObjectListItem(
      form,
      index,
      rentalObject?.rentalId,
      rentalObject?.street,
      rentalObject?.rentalObjectTypeName,
      rentalObject?.propertyNumberName,
      rentalObject?.companyNumberName,
      this.categories.find(item => item.value === category)?.name,
      LocalDate.fromDate(form.controls['fromDate'].value),
      LocalDate.fromDate(form.controls['toDate'].value)
    );
  }

  private isHistorical(row: RentalObjectListItem): boolean {
    return !!row.toDate?.hasValue() && row.toDate.valueOf() <= LocalDate.today().valueOf();
  }
}
